import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  API_SESSION_MISSING_EVENT,
  API_SESSION_RESTORED_EVENT,
  isApiSessionError,
  openApiSignIn,
  probeApiSession,
} from '../../services/internalApi';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Shown when the browser has no Cloudflare Access session for the internal API host.
 * Access binds its cookie to the hostname, so the user signs in to the API once
 * in a new tab; we poll until the API answers and then tell the app to reload.
 */
function ApiSessionBanner() {
  const [visible, setVisible] = useState(false);
  const [polling, setPolling] = useState(false);
  const [checking, setChecking] = useState(false);
  const pollTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPolling(false);
  }, []);

  const markRestored = useCallback(() => {
    stopPolling();
    setVisible(false);
    window.dispatchEvent(new Event(API_SESSION_RESTORED_EVENT));
  }, [stopPolling]);

  useEffect(() => {
    let cancelled = false;
    probeApiSession().catch((err) => {
      if (!cancelled && isApiSessionError(err)) setVisible(true);
    });

    const onMissing = () => setVisible(true);
    window.addEventListener(API_SESSION_MISSING_EVENT, onMissing);
    return () => {
      cancelled = true;
      window.removeEventListener(API_SESSION_MISSING_EVENT, onMissing);
    };
  }, []);

  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  const startPolling = () => {
    stopPolling();
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let inFlight = false;
    setPolling(true);
    pollTimerRef.current = setInterval(() => {
      if (Date.now() > deadline) {
        stopPolling();
        return;
      }
      if (inFlight) return;
      inFlight = true;
      probeApiSession()
        .then(markRestored)
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    }, POLL_INTERVAL_MS);
  };

  const handleSignIn = () => {
    openApiSignIn();
    startPolling();
  };

  const handleRetry = () => {
    setChecking(true);
    probeApiSession()
      .then(markRestored)
      .catch(() => {})
      .finally(() => setChecking(false));
  };

  if (!visible) return null;

  return (
    <div
      className="mb-6 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <i className="material-icons-outlined shrink-0 pt-0.5 text-lg text-amber-600">lock</i>
        <div className="text-sm leading-snug">
          <p className="font-medium">You're not signed in to the internal API in this browser.</p>
          {polling && (
            <p className="mt-0.5 text-amber-800/80">Waiting for sign-in to finish in the other tab…</p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleRetry}
          className="btn-secondary px-3 py-2 text-sm"
          disabled={checking}
        >
          Retry
        </button>
        <button
          type="button"
          onClick={handleSignIn}
          className="btn-primary flex items-center gap-2 px-3 py-2 text-sm"
        >
          {polling ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <i className="material-icons-outlined text-base">open_in_new</i>
          )}
          <span>Sign in to the API</span>
        </button>
      </div>
    </div>
  );
}

export default ApiSessionBanner;
